import React, { createContext, useContext } from 'react';
import { Split } from 'lucide-react';
import { Tooltip } from '@cotera/client/app/components';
import type { WorkflowStructureIssuesByNodeId } from '../workflow-structure-issues';

const EMPTY: WorkflowStructureIssuesByNodeId = new Map();

/**
 * Structural warnings for the whole graph, computed once at the canvas and read
 * per node — the wiring mistakes a node makes (a group-by with no for-each
 * below it), separate from a stale ref (see `WorkflowRefIssuesProvider`).
 */
const WorkflowStructureIssuesContext =
  createContext<WorkflowStructureIssuesByNodeId>(EMPTY);

export const WorkflowStructureIssuesProvider: React.FC<{
  readonly issues: WorkflowStructureIssuesByNodeId;
  readonly children: React.ReactNode;
}> = ({ issues, children }) => (
  <WorkflowStructureIssuesContext.Provider value={issues}>
    {children}
  </WorkflowStructureIssuesContext.Provider>
);

/** The structural warnings on one node, empty when it has none. */
export function useNodeStructureIssues(nodeId: string): readonly string[] {
  return useContext(WorkflowStructureIssuesContext).get(nodeId) ?? [];
}

/**
 * The mark a node carries when it is wired in a way that will not do what it
 * looks like it does — today, a group-by with no for-each below it.
 *
 * Distinct from the stale-ref mark: this is about the shape of the graph, not a
 * value that went missing, so it reads as a hint ("add a step") rather than
 * "fix a value". The group-by icon ties it to the node it is about.
 */
export const WorkflowNodeStructureIssueMark: React.FC<{
  readonly messages: readonly string[];
}> = ({ messages }) => {
  if (messages.length === 0) {
    return null;
  }
  return (
    <Tooltip
      side="top"
      tooltipContent={
        <span className="flex flex-col gap-1">
          {messages.map((message) => (
            <span key={message}>{message}</span>
          ))}
        </span>
      }
    >
      <span
        className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-blue-300 bg-blue-50 text-blue-800"
        role="alert"
        aria-label="Wiring suggestion"
      >
        <Split className="size-3" />
      </span>
    </Tooltip>
  );
};
